import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useGroups, useReorderGroups, useArchivedGroups, useSetGroupArchived } from '@/hooks/useGroups';
import { useDashboardBalances } from '@/hooks/useDashboardBalances';
import { useDashboardBudgets } from '@/hooks/useDashboardBudgets';
import { useRecentActivities, useActivitiesRealtimeSubscription } from '@/hooks/useActivities';
import { useDashboardRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { GroupCard } from '@/components/groups/GroupCard';
import { CreateGroupDialog } from '@/components/groups/CreateGroupDialog';
import { ActivityItem } from '@/components/activities/ActivityItem';
import { formatActivity, navigateToActivity } from '@/lib/activityFormatter';
import { useNavigate, NavLink } from 'react-router';
import { Users, Plus, Loader2, Link as LinkIcon, Activity, ArrowRight, GripVertical, Archive, ArchiveRestore, ChevronDown } from 'lucide-react';
import { sortGroupsForUser } from '@/lib/sortGroups';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { GroupWithMembers } from '@/api/groups';
import type { GroupStats } from '@/api/balances';
import type { UserBalance } from '@/hooks/useBalances';
import type { BudgetStatus } from '@/lib/budget';
import { useTranslation } from 'react-i18next';

function SortableGroupCard({
  group,
  userBalance,
  groupStats,
  isBalanceLoading,
  budgetStatus,
}: {
  group: GroupWithMembers;
  userBalance?: UserBalance;
  groupStats?: GroupStats;
  isBalanceLoading?: boolean;
  budgetStatus?: BudgetStatus | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={`relative ${isDragging ? 'z-10 opacity-50' : ''}`}
    >
      <button
        className="absolute top-2 right-2 z-10 rounded-md p-1.5 text-text-quaternary hover:text-text-secondary hover:bg-surface-hover cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <GroupCard
        group={group}
        userBalance={userBalance}
        groupStats={groupStats}
        isBalanceLoading={isBalanceLoading}
        budgetStatus={budgetStatus}
        disableNavigation
      />
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const { data: groups, isLoading, error } = useGroups();
  const { data: dashboardData, isLoading: balancesLoading } = useDashboardBalances(groups);
  const { data: budgetStatuses } = useDashboardBudgets(groups);
  const navigate = useNavigate();
  const { data: recentActivityRows, isLoading: activitiesLoading } = useRecentActivities(10, groups?.map(g => g.id));
  useActivitiesRealtimeSubscription();
  useDashboardRealtimeSubscription();
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [showJoinInput, setShowJoinInput] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const reorderMutation = useReorderGroups();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortedGroups = useMemo(
    () => (groups && user ? sortGroupsForUser(groups, user.id) : groups),
    [groups, user]
  );

  const recentActivities = useMemo(() => {
    if (!recentActivityRows || !user) return [];
    return recentActivityRows.map(row => formatActivity(row, user.id));
  }, [recentActivityRows, user]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !sortedGroups) return;

    const oldIndex = sortedGroups.findIndex(g => g.id === active.id);
    const newIndex = sortedGroups.findIndex(g => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedGroups, oldIndex, newIndex);
    reorderMutation.mutate(
      reordered.map((g, i) => ({ groupId: g.id, displayOrder: i }))
    );
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">
          {profile?.display_name
            ? t('web:dashboardPage.welcomeBackName', { name: profile.display_name.split(' ')[0] })
            : t('web:loginPage.welcomeBack')}
        </h1>
        <p className="mt-1 text-text-secondary">{t('web:dashboardPage.hereSAnOverviewOfYour')}</p>
      </div>

      {/* Groups section */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">{t('web:dashboardPage.yourGroups')}</h2>
          <div className="flex items-center gap-2">
            {sortedGroups && sortedGroups.length >= 2 && (
              <button
                onClick={() => setIsReorderMode(!isReorderMode)}
                className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  isReorderMode
                    ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-600'
                    : 'border-border-secondary text-text-secondary hover:bg-surface-hover'
                }`}
              >
                <GripVertical className="h-4 w-4" />
                {isReorderMode ? t('common:button.done') : t('web:dashboardPage.reorder')}
              </button>
            )}
            <button
              onClick={() => setShowJoinInput(!showJoinInput)}
              className="flex items-center gap-1 rounded-lg border border-border-secondary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors"
            >
              <LinkIcon className="h-4 w-4" />
              {t('expenses:qrScanner.alert.join')}
            </button>
            <button
              onClick={() => setShowCreateGroup(true)}
              className="flex items-center gap-1 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('web:dashboardPage.newGroup')}
            </button>
          </div>
        </div>

        {/* Join group input */}
        {showJoinInput && (
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={joinCode}
              onChange={e => setJoinCode(e.target.value)}
              placeholder={t('web:dashboardPage.enterInviteCode')}
              className="flex-1 rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
            <button
              onClick={() => {
                if (joinCode.trim()) {
                  navigate(`/join/${joinCode.trim()}`);
                }
              }}
              disabled={!joinCode.trim()}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {t('expenses:qrScanner.alert.join')}
            </button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="mt-6 flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {t('web:dashboardPage.failedToLoadGroups', { message: error instanceof Error ? error.message : t('web:common.unknownError') })}
          </div>
        )}

        {/* Groups grid */}
        {sortedGroups && sortedGroups.length > 0 && (
          isReorderMode ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortedGroups.map(g => g.id)} strategy={rectSortingStrategy}>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {sortedGroups.map(group => (
                    <SortableGroupCard
                      key={group.id}
                      group={group}
                      userBalance={dashboardData?.balances.get(group.id)}
                      groupStats={dashboardData?.stats.get(group.id)}
                      isBalanceLoading={balancesLoading}
                      budgetStatus={budgetStatuses?.get(group.id) ?? null}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedGroups.map(group => (
                <GroupCard
                  key={group.id}
                  group={group}
                  userBalance={dashboardData?.balances.get(group.id)}
                  groupStats={dashboardData?.stats.get(group.id)}
                  isBalanceLoading={balancesLoading}
                  budgetStatus={budgetStatuses?.get(group.id) ?? null}
                />
              ))}
            </div>
          )
        )}

        {/* Archived groups — collapsible, with inline unarchive */}
        <ArchivedGroupsSection />

        {/* Empty state */}
        {sortedGroups && sortedGroups.length === 0 && (
          <div className="mt-4 flex flex-col items-center rounded-xl border-2 border-dashed border-border-secondary bg-surface-primary p-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/40">
              <Users className="h-6 w-6 text-brand-600" />
            </div>
            <h3 className="mt-4 text-sm font-medium text-text-primary">{t('groups:empty.noGroups')}</h3>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('web:dashboardPage.createAGroupToStartSplitting')}
            </p>
            <button
              onClick={() => setShowCreateGroup(true)}
              className="mt-4 flex items-center gap-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('web:dashboardPage.createYourFirstGroup')}
            </button>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">{t('home:section.recentActivity')}</h2>
          <NavLink
            to="/activities"
            className="flex items-center gap-1 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
          >
            {t('home:section.viewAll')}
            <ArrowRight className="h-4 w-4" />
          </NavLink>
        </div>

        {activitiesLoading && (
          <div className="mt-4 flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        )}

        {!activitiesLoading && recentActivities.length > 0 && (
          <div className="mt-4 divide-y divide-border-tertiary overflow-hidden rounded-xl bg-surface-primary shadow-sm ring-1 ring-border-primary">
            {recentActivities.map(a => (
              <ActivityItem
                key={a.id}
                activity={a}
                onClick={() => navigateToActivity(a, navigate)}
              />
            ))}
          </div>
        )}

        {!activitiesLoading && recentActivities.length === 0 && (
          <div className="mt-4 flex flex-col items-center rounded-xl bg-surface-primary p-8 shadow-sm ring-1 ring-border-primary text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-tertiary">
              <Activity className="h-5 w-5 text-text-quaternary" />
            </div>
            <p className="mt-3 text-sm text-text-tertiary">{t('home:empty.noActivity')}</p>
          </div>
        )}
      </div>

      <CreateGroupDialog
        open={showCreateGroup}
        onClose={() => setShowCreateGroup(false)}
        onCreated={(groupId) => navigate(`/groups/${groupId}`)}
      />
    </div>
  );
}

/**
 * Collapsible list of the user's archived groups with inline unarchive.
 * Renders nothing when there are no archived groups. Archiving is the only way
 * a group leaves the main list, so this is how it gets back.
 */
function ArchivedGroupsSection() {
  const { t } = useTranslation();
  const { data: archived } = useArchivedGroups();
  const setArchived = useSetGroupArchived();
  const [expanded, setExpanded] = useState(false);

  if (!archived || archived.length === 0) return null;

  return (
    <div className="mt-6 border-t border-border-tertiary pt-4">
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-text-primary"
      >
        <Archive className="h-4 w-4" />
        {t('web:dashboardPage.archivedGroups', { count: archived.length })}
        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <ul className="mt-3 space-y-2">
          {archived.map(group => (
            <li
              key={group.id}
              className="flex items-center justify-between rounded-lg border border-border-secondary px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: group.color ?? '#6366F1' }}
                />
                <span className="truncate text-sm text-text-primary">{group.name}</span>
              </div>
              <button
                onClick={() => setArchived.mutate({ groupId: group.id, archived: false })}
                disabled={setArchived.isPending}
                className="shrink-0 flex items-center gap-1.5 rounded-lg border border-border-secondary px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-hover disabled:opacity-50"
              >
                <ArchiveRestore className="h-3.5 w-3.5" />
                {t('groups:archived.action.unarchive')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
